import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIsFetching } from 'react-query';
import PulseLoader from 'react-spinners/PulseLoader';
import { Button, Col, Row } from 'reactstrap';

import { addDays, differenceInCalendarDays, isSameDay, isValid, subDays } from 'date-fns';
import ArrowLeftIcon from 'material-react-icons/KeyboardArrowLeft';
import ArrowRightIcon from 'material-react-icons/KeyboardArrowRight';

import classNames from '../../helpers/classNames';
import { formatISODate } from '../../helpers/formatISODate';
import usePrevious from '../../hooks/usePrevious';
import { MappedGolfCourseType } from '../../services/apis/golfCourses/types';
import { QUERY_KEY_TEETIMES } from '../../services/apis/teeTimes/api';
import DatesTableItem from '../DatesTableItem/DatesTableItem';
import styles from './DatesTable.module.scss';

const today = new Date();

interface DatesTableProps {
  golfCourse: MappedGolfCourseType;
  selectedDate: string;
  shrink?: boolean;
  onSelectedDateChange: (date: string) => void;
}

const DatesTable: FC<DatesTableProps> = props => {
  const { golfCourse, selectedDate, shrink, onSelectedDateChange } = props;
  const prevSelectedDate = usePrevious(selectedDate);
  const lastChangedDates = useRef(`${prevSelectedDate}_${selectedDate}`);
  const [cDate, setCDate] = useState(selectedDate);
  const isFetching = !!useIsFetching([QUERY_KEY_TEETIMES, golfCourse.golfCourse.id, cDate]);

  const dates = useMemo(() => {
    const datesArray: string[] = [];
    let initialDate = new Date(`${cDate || selectedDate}T00:00:00`);
    const isValidDate = isValid(initialDate) && initialDate.getFullYear() >= 2022;

    if (!isValidDate) {
      initialDate = today;
    }

    let dateF = new Date(initialDate);
    let dateB = new Date(initialDate);
    datesArray.push(formatISODate(dateB));
    for (let i = 1; i < 4; i++) {
      dateB = subDays(dateB, 1);
      dateF = addDays(dateF, 1);
      datesArray.unshift(formatISODate(dateB));
      datesArray.push(formatISODate(dateF));
    }

    return datesArray;
  }, [selectedDate, cDate]);

  useEffect(() => {
    // if selectedDate was changed externally
    if (
      lastChangedDates.current !== `${prevSelectedDate}_${selectedDate}` &&
      !dates.find(date => isSameDay(new Date(date), new Date(selectedDate)))
    ) {
      setCDate(selectedDate);
    }
    lastChangedDates.current = `${prevSelectedDate}_${selectedDate}`;
  }, [dates, selectedDate, prevSelectedDate]);

  const moveDatesForward = () => {
    const d = cDate || selectedDate;
    const cd = formatISODate(addDays(new Date(`${d}T00:00:00`), 1));
    setCDate(cd);
  };

  const moveDatesBackward = () => {
    const d = cDate || selectedDate;
    const cd = formatISODate(subDays(new Date(`${d}T00:00:00`), 1));
    setCDate(cd);
  };

  return (
    <div className="fade show position-relative my-4 px-0">
      <div className={classNames('d-flex', isFetching && 'opacity-25')}>
        <Button className="btn-no-padding" color="transparent">
          <ArrowLeftIcon
            color="#7ca324"
            size={40}
            viewBox="5.5 5.3 13 13"
            onClick={moveDatesBackward}
          />
        </Button>

        <Row
          className={classNames(
            'flex-grow-1 px-2 flex-nowrap',
            shrink ? styles.shrink : styles.dates
          )}
        >
          {dates?.map(date => {
            const isActive = date === selectedDate;
            const isDisabled =
              differenceInCalendarDays(new Date(date), new Date(formatISODate(today))) < 0;

            return (
              <Col key={date} className="px-1 px-md-2">
                <DatesTableItem
                  date={date}
                  active={isActive}
                  disabled={isDisabled}
                  loading={isFetching}
                  onClick={isActive || isDisabled ? undefined : () => onSelectedDateChange(date)}
                />
              </Col>
            );
          })}
        </Row>

        <Button className="btn-no-padding" color="transparent">
          <ArrowRightIcon
            color="#7ca324"
            size={40}
            viewBox="5.5 5.3 13 13"
            onClick={moveDatesForward}
          />
        </Button>
      </div>
      <div
        className={classNames(
          'position-absolute top-50 start-50 translate-middle fade pe-none',
          isFetching && 'show'
        )}
      >
        <PulseLoader size={8} />
      </div>
    </div>
  );
};

export default DatesTable;
